import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LOG_ACTION_TYPE_MAP } from 'src/utils';
import { QUERY_KEY_MAP } from 'src/constants';
import { useNotification } from 'src/hooks/use-notification';
import { createOrUpdateStoresPromise } from 'src/utils/api/stores';
import { StoreConfigSchema } from 'src/utils/api/stores/storesSchema';
import { useLogAction } from '../use-log-action';
import { shouldShowStoreError } from './shouldShowStoreError';

export const useUpdateStore = () => {
  const queryClient = useQueryClient();

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createOrUpdateStoresPromise,
  });

  const { logUserAction } = useLogAction();

  const updateStoreAsync = async (params: StoreConfigSchema) => {
    try {
      const res = await mutateAsync(params);

      if (shouldShowStoreError(res)) {
        enqueueErrorNotification(res ?? 'Error Updating Store');
        return false;
      }

      await Promise.all([
        logUserAction({
          action: 'update_store',
          action_from: '',
          action_to: '',
          type: LOG_ACTION_TYPE_MAP.STORE_MANAGEMENT,
          type_metadata: JSON.stringify({
            uuid: params.store_id || '',
            updated_fields: Object.keys(params).filter(
              (key) => key !== 'uuid' && key !== 'request_type' && key !== 'store_id'
            ),
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useGetStore, params.store_id],
        }),
      ]);

      // does not need to be invalidated within the promise as useGetStore gets specific store details now.
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY_MAP.useListStores] });

      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MAP.useListAuditLogs],
      });

      enqueueSuccessNotification('Store updated successfully');
      return true;
    } catch (error) {
      enqueueErrorNotification('Error while updating store');

      console.error('updateStoreAsync', error);
      return false;
    }
  };

  return {
    updateStoreAsync,
    isLoading: isPending,
  };
};
